/**
 * HOME DASHBOARD
 *
 * Production-quality driver home screen.
 * - Greeting + status indicator
 * - Active delivery banner (if in progress)
 * - Earnings summary cards
 * - Quick actions
 * - Recent activity
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView,
  Platform, RefreshControl, ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSelector, useDispatch } from 'react-redux';
import { spacing, fonts, RD } from '../../theme/colors';
import { RootState, setOnline as setOnlineAction, setAccountOnHold, addActiveJob, setActiveJobs } from '../../store';
import { api } from '../../services/api';
import { wooService } from '../../services/woocommerce';
import { connectSocket, disconnectSocket } from '../../services/socket';
import * as Location from 'expo-location';
// Earnings now come from Redux store (persisted via AsyncStorage)
import { JobOfferBanner } from '../../components/JobOfferBanner';
import { PickupReminderModal } from '../../components/PickupReminderModal';
import { WelcomeModal } from '../../components/WelcomeModal';
import { useSupportNotifications } from '../../services/support-notifications';
import { PREDISPATCH_ENABLED } from '../../config/flags';
import { predispatchLaunchGuard } from '../../services/predispatch-deeplink';
import { pickupWindowSortKey, driverWindowStart, fmtDriverWindow } from '../../utils/windows';

// Module-level flag so the reminder shows once per app launch (resets on cold load).
let pickupReminderShownThisLaunch = false;
// Pre-dispatch gate is only auto-presented once per app launch. The guard is SHARED
// with the deep-link handler (predispatchLaunchGuard.checked): when the SMS magic
// link already routed the driver into the checklist, this popup defers and never
// double-opens for the same job.

export function HomeScreen({ navigation }: any) {
  const dispatch = useDispatch();
  const driver = useSelector((s: RootState) => s.auth.driver);
  const online = useSelector((s: RootState) => s.jobs.online);
  const activeJobs = useSelector((s: RootState) => s.jobs.activeJobs);
  const activeJob = activeJobs.length > 0 ? activeJobs[0] : null;
  const reduxEarnings = useSelector((s: RootState) => s.jobs.earnings);
  const reduxCompletedCount = useSelector((s: RootState) => s.jobs.completedJobs.length);
  const [apiEarnings, setApiEarnings] = useState<any>(null);
  const [apiCompletedCount, setApiCompletedCount] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [newJobOffer, setNewJobOffer] = useState<any>(null);
  const [wooSynced, setWooSynced] = useState<boolean | null>(null);
  const [showA2HS, setShowA2HS] = useState(false);
  const [showPickupReminder, setShowPickupReminder] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  // Becomes true once the AsyncStorage welcome-seen check has resolved, so the
  // pickup reminder waits for that decision instead of racing the async read.
  const [welcomeChecked, setWelcomeChecked] = useState(false);
  const { unreadCount: supportUnread } = useSupportNotifications();

  // "How Commonplace works" — shown ONCE, on the very first app open. Persist a
  // flag in AsyncStorage so it never reappears on later launches.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const seen = await AsyncStorage.getItem('@cp_welcome_seen');
        if (!cancelled && !seen) setShowWelcome(true);
      } catch {}
      finally { if (!cancelled) setWelcomeChecked(true); }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismissWelcome = useCallback(() => {
    setShowWelcome(false);
    AsyncStorage.setItem('@cp_welcome_seen', '1').catch(() => {});
  }, []);

  // Pre-dispatch gate (flag-gated). When the app opens with an assigned, not-yet-
  // started job whose pre-dispatch checklist is still PENDING, present the
  // PreDispatchChecklistScreen as a blocking modal before the normal pickup
  // reminder. If the checklist was already PASSED (e.g. via the SMS magic link the
  // night before / morning), we skip it entirely and let the pickup reminder show.
  // gateResolved flips true once we've made that decision so the reminder waits.
  const [gateResolved, setGateResolved] = useState(!PREDISPATCH_ENABLED);
  useEffect(() => {
    if (!PREDISPATCH_ENABLED) return; // feature off: behavior unchanged
    // Already handled this launch — either by us earlier, or by the deep-link
    // handler routing the driver into the checklist via the SMS magic link.
    if (predispatchLaunchGuard.checked) { setGateResolved(true); return; }
    if (!welcomeChecked || showWelcome) return; // wait for first-launch welcome
    predispatchLaunchGuard.checked = true;

    // A job that's been claimed but not yet en-route to the seller is the one a
    // driver would clear a pre-dispatch checklist for. Skip jobs already past
    // pickup, and never gate a job that's been reassigned to a fleet driver.
    const PRE_PICKUP = ['ACCEPTED', 'ASSIGNED', 'SCHEDULED', 'CONFIRMED'];
    const candidate = (activeJobs || []).find((j: any) =>
      PRE_PICKUP.includes(j.status)
      && (!j.assignedFleetDriverId || j.assignedFleetDriverId === driver?.id),
    );

    if (!candidate) { setGateResolved(true); return; }

    let cancelled = false;
    api.get(`/jobs/${candidate.id}/predispatch`)
      .then(({ data }) => {
        if (cancelled) return;
        // Only open for a REAL enabled checklist that isn't done. When the backend
        // kill switch is off the GET returns 404 (no status) — bail, don't open.
        const pending = !!data?.status && data.status !== 'PASSED' && !data?.completedAt;
        // ...and only when the SERVER says it is due: scheduled, today or tomorrow in the
        // job's timezone. Without this the modal blocked the driver's way to Home for a job
        // nine days out, or one with no schedule at all — friction on every delivery rather
        // than preparation for the next one. The night-before / morning texts have always
        // used this horizon; the popup is now the same rule rather than a second one.
        if (pending && data?.due === true) {
          // Present as a blocking modal — the driver cannot reach Home until they
          // complete it or explicitly defer (the screen's own back/notify paths).
          navigation.navigate('PreDispatchChecklist', { jobId: candidate.id, job: candidate });
        }
        setGateResolved(true);
      })
      .catch(() => { if (!cancelled) setGateResolved(true); }); // fail open — never block on a network error
    return () => { cancelled = true; };
  }, [welcomeChecked, showWelcome, activeJobs, driver?.id, navigation]);

  // Show the pickup/expectations reminder once each time the app is opened, but
  // never while the first-launch Welcome modal is up (it would render behind the
  // full-screen Welcome and get dismissed unseen). Wait until Welcome is closed,
  // and until the pre-dispatch gate decision has resolved.
  useEffect(() => {
    if (!welcomeChecked || showWelcome || !gateResolved) return;
    if (!pickupReminderShownThisLaunch) {
      pickupReminderShownThisLaunch = true;
      setShowPickupReminder(true);
    }
  }, [welcomeChecked, showWelcome, gateResolved]);

  // Location permission — prompt on HomeScreen so ALL drivers (including existing ones) get asked immediately
  const [locationGranted, setLocationGranted] = useState<boolean | null>(null);
  const [locationDenied, setLocationDenied] = useState(false);

  const requestLocationPermission = useCallback(() => {
    if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.geolocation) {
      const onOk = (pos: any) => {
        setLocationGranted(true);
        setLocationDenied(false);
        // Report initial location to backend
        api.post('/drivers/me/location', {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }).catch(() => {});
      };
      // err.code: 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT.
      // Only a true denial (code 1) means "blocked". A timeout/unavailable is usually
      // a slow/indoor GPS fix — retry once leniently before giving up, and DON'T flag
      // it as denied (that's what made drivers think they couldn't enable location).
      const onErr = (err: any) => {
        if (err && err.code === 1) { setLocationGranted(false); setLocationDenied(true); return; }
        navigator.geolocation.getCurrentPosition(
          onOk,
          (e2: any) => {
            if (e2 && e2.code === 1) { setLocationGranted(false); setLocationDenied(true); }
            else { setLocationGranted(false); setLocationDenied(false); } // couldn't get a fix; show soft "Allow" to retry
          },
          { enableHighAccuracy: false, timeout: 30000, maximumAge: 600000 },
        );
      };
      // Low accuracy = fast, network-based, works indoors; plenty precise for radius matching.
      navigator.geolocation.getCurrentPosition(onOk, onErr, { enableHighAccuracy: false, timeout: 20000, maximumAge: 300000 });
    } else {
      (async () => {
        try {
          const { status } = await Location.requestForegroundPermissionsAsync();
          if (status === 'granted') {
            setLocationGranted(true);
            setLocationDenied(false);
            const pos = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Balanced });
            api.post('/drivers/me/location', {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
            }).catch(() => {});
          } else {
            setLocationGranted(false);
            setLocationDenied(true);
          }
        } catch {
          setLocationGranted(false);
          setLocationDenied(true);
        }
      })();
    }
  }, []);

  // Auto-check location permission on mount
  useEffect(() => {
    if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' as PermissionName }).then((result) => {
        if (result.state === 'granted') {
          requestLocationPermission(); // silently get position
        } else if (result.state === 'denied') {
          setLocationGranted(false);
          setLocationDenied(true);
        } else {
          // 'prompt' — show the banner, wait for user tap
          setLocationGranted(false);
        }
      }).catch(() => setLocationGranted(false));
    } else if (Platform.OS !== 'web') {
      requestLocationPermission();
    }
  }, [requestLocationPermission]);

  // NOTE: the periodic location-reporting loop now lives app-wide in RootNavigator
  // (sendLocationNow heartbeat) so it runs on EVERY screen, not just Home — previously a
  // driver leaving the Home tab stopped all location updates, which is why admin saw "no
  // location in days/weeks". HomeScreen still requests permission (below) for the banner.

  // Add to Home Screen prompt — show every session until installed
  const driverId = driver?.id;
  const deferredPromptRef = useRef<any>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [hasNativePrompt, setHasNativePrompt] = useState(false);

  const dismissA2HS = () => setShowA2HS(false);

  const handleAddToHomeScreen = async () => {
    if (deferredPromptRef.current) {
      try {
        deferredPromptRef.current.prompt();
        const result = await deferredPromptRef.current.userChoice;
        if (result.outcome === 'accepted') {
          setShowA2HS(false);
          try { localStorage.setItem('__a2hs_installed', '1'); } catch {}
        }
      } catch {}
      deferredPromptRef.current = null;
    }
  };

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !driverId) return;

    // Already installed as standalone app
    const isStandalone = (window.navigator as any).standalone === true
      || window.matchMedia('(display-mode: standalone)').matches;
    if (isStandalone) return;

    // Already installed via prompt
    try { if (localStorage.getItem('__a2hs_installed') === '1') return; } catch {}

    // Detect iOS Safari
    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const safari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS/.test(ua);
    setIsIOS(ios && safari);

    // Chrome/Android: capture beforeinstallprompt
    const handleBeforeInstall = (e: any) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setHasNativePrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show after short delay
    const timer = setTimeout(() => setShowA2HS(true), 1500);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [driverId]);

  // Use API data when available, Redux as fallback
  const earnings = apiEarnings || reduxEarnings;
  const completedCount = apiCompletedCount ?? reduxCompletedCount;

  // Keep the latest merged earnings in a ref so loadData can read current
  // fallback values without depending on them (avoids refetch on every
  // earnings change / stale-closure refetch cycles).
  const earningsRef = useRef(earnings);
  earningsRef.current = earnings;

  const loadData = useCallback(async () => {
    const cur = earningsRef.current;
    // Fetch earnings from backend
    try {
      const [earningsRes, jobsRes] = await Promise.all([
        api.get('/earnings').catch(() => ({ data: null })),
        api.get('/drivers/me/jobs').catch(() => null),   // null = fetch failed
      ]);
      if (earningsRes.data) {
        setApiEarnings({
          todayEarnings: earningsRes.data.todayEarnings ?? cur.todayEarnings,
          weekEarnings: earningsRes.data.weekEarnings ?? cur.weekEarnings,
          pendingBalance: earningsRes.data.pendingBalance ?? cur.pendingBalance,
          totalEarnings: earningsRes.data.totalPaid ?? earningsRes.data.totalEarnings ?? cur.totalEarnings,
          completedCount: earningsRes.data.completedCount ?? cur.completedCount,
        });
      }
      // Only reconcile jobs when the fetch actually succeeded, so a network blip never wipes
      // the active list.
      if (jobsRes && Array.isArray(jobsRes.data)) {
        const allJobs = jobsRes.data;
        // A job is delivered/done once the driver finishes delivery
        // (BUYER_SIGNATURE_COMPLETE); admin COMPLETED comes later. Count both so
        // the home tally matches the earnings/history screens.
        const DELIVERED_STATUSES = ['BUYER_SIGNATURE_COMPLETE', 'COMPLETED'];
        const completed = allJobs.filter((j: any) => DELIVERED_STATUSES.includes(j.status));
        if (completed.length > 0 || allJobs.length > 0) {
          setApiCompletedCount(completed.length);
        }
        // The backend is the source of truth for THIS driver's jobs. REPLACE the active list
        // (not just add to it) so a job the driver was UNASSIGNED from drops off the home
        // screen and goes back to the available board, instead of lingering as 'accepted'.
        const active = allJobs.filter((j: any) =>
          !['AVAILABLE', 'COMPLETED', 'CANCELLED'].includes(j.status)
        );
        dispatch(setActiveJobs(active));
      }
    } catch {}
    // Check if hold has been lifted by admin
    try {
      const holdRes = await api.get('/training/hold-status');
      if (holdRes.data && !holdRes.data.onHold) {
        dispatch(setAccountOnHold(false));
      }
    } catch {}
    // Sync WooCommerce orders (non-blocking)
    try {
      await wooService.syncOrders();
      setWooSynced(true);
    } catch {
      setWooSynced(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Listen for fleet job assignments via socket — fleet members get notified of new jobs
  useEffect(() => {
    try {
      const s = connectSocket();
      s.on('job:fleet-assigned', () => {
        // Reload data to pick up newly assigned job
        loadData();
      });
      return () => {
        try { s.off('job:fleet-assigned'); } catch {}
      };
    } catch {
      return undefined;
    }
  }, [loadData]);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadData();
    // Minimum 800ms to show visual feedback
    await new Promise(r => setTimeout(r, 800));
    setRefreshing(false);
  };

  const toggleOnline = () => {
    const next = !online;
    dispatch(setOnlineAction(next));
    api.post('/drivers/me/status', { online: next }).catch(() => {});
    // When going online, trigger a WooCommerce sync to look for new jobs
    if (next && !activeJob) {
      wooService.syncOrders().then(() => setWooSynced(true)).catch(() => setWooSynced(false));
    }
  };

  const firstName = driver?.name?.split(' ')[0] || 'Driver';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <View style={{ flex: 1 }}>
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={RD.ink} />}
    >
      {/* Refresh indicator / button for web where RefreshControl doesn't work */}
      {Platform.OS === 'web' && !refreshing && (
        <TouchableOpacity style={styles.refreshRow} onPress={onRefresh} activeOpacity={0.7}>
          <Ionicons name="refresh-outline" size={16} color={RD.textFaint} />
          <Text style={styles.refreshText}>Tap to refresh</Text>
        </TouchableOpacity>
      )}
      {refreshing && (
        <View style={styles.refreshRow}>
          <ActivityIndicator size="small" color={RD.ink} />
          <Text style={styles.refreshText}>Refreshing...</Text>
        </View>
      )}

      {/* Header — greeting (online/offline removed: jobs are always available on open) */}
      <View style={styles.header}>
        <View style={styles.headerNameWrap}>
          <Text style={styles.greeting}>{greeting},</Text>
          <View style={styles.nameRow}>
            <Text style={styles.name}>{firstName}</Text>
            {supportUnread > 0 && (
              <View style={styles.nameSupportBadge}>
                <Ionicons name="chatbubble-ellipses" size={11} color="#fff" />
                <Text style={styles.nameSupportBadgeText}>{supportUnread > 9 ? '9+' : supportUnread}</Text>
              </View>
            )}
          </View>
        </View>
        {supportUnread > 0 && (
          <TouchableOpacity
            style={styles.supportBell}
            onPress={() => navigation.navigate('SupportInbox')}
            activeOpacity={0.7}
          >
            <Ionicons name="notifications" size={20} color={RD.maroon} />
            <View style={styles.supportBellDot}>
              <Text style={styles.supportBellDotText}>{supportUnread > 9 ? '9+' : supportUnread}</Text>
            </View>
          </TouchableOpacity>
        )}
      </View>

      {/* Location Permission Banner — shown until driver grants location */}
      {locationGranted === false && (
        <TouchableOpacity
          style={{
            marginBottom: 16,
            padding: 16,
            backgroundColor: locationDenied ? RD.dangerSoft : RD.infoSoft,
            borderRadius: 18,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 12,
            borderWidth: 1,
            borderColor: locationDenied ? RD.danger : RD.info,
          }}
          onPress={requestLocationPermission}
          activeOpacity={0.7}
        >
          <View style={{
            width: 44, height: 44, borderRadius: 22,
            backgroundColor: RD.surface,
            justifyContent: 'center', alignItems: 'center',
          }}>
            <Ionicons name="location" size={24} color={locationDenied ? RD.danger : RD.info} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 15, fontWeight: '700', color: locationDenied ? RD.danger : RD.info }}>
              {locationDenied ? 'Location Access Required' : 'Enable Location'}
            </Text>
            <Text style={{ fontSize: 12, color: RD.textMuted, marginTop: 2 }}>
              {locationDenied
                ? 'Location is required for deliveries. Open browser settings to allow access, then tap here.'
                : 'Tap to enable location — required for accepting and completing deliveries.'}
            </Text>
          </View>
          <View style={{
            paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999,
            backgroundColor: locationDenied ? RD.danger : RD.info,
          }}>
            <Text style={{ fontSize: 13, fontWeight: '700', color: '#fff' }}>
              {locationDenied ? 'Retry' : 'Allow'}
            </Text>
          </View>
        </TouchableOpacity>
      )}

      {/* "This week" earnings hero — dark card. Hidden for fleet members who don't see payouts */}
      {!(driver as any)?.fleetManagerId && (
        <View style={styles.heroCard}>
          <Text style={styles.heroEyebrow}>This week</Text>
          <View style={styles.heroAmountRow}>
            <Text style={styles.heroAmount}>
              ${Math.floor(earnings.weekEarnings).toLocaleString()}
            </Text>
            <Text style={styles.heroAmountCents}>
              .{(earnings.weekEarnings % 1).toFixed(2).slice(2)}
            </Text>
          </View>
          <Text style={styles.heroSub}>
            {completedCount} {completedCount === 1 ? 'delivery' : 'deliveries'} completed
          </Text>
        </View>
      )}

      {/* Active delivery card(s) — sorted by soonest PICKUP time; jobs scheduled
          for TODAY get a blue outline so they stand out. */}
      {[...activeJobs]
        .sort((a: any, b: any) => pickupWindowSortKey(a) - pickupWindowSortKey(b))
        .map((aj: any) => (
          <ActiveJobCard
            key={aj.id}
            activeJob={aj}
            scheduledToday={isScheduledToday(driverWindowStart(aj)?.toISOString(), aj.pickupTimezone)}
            hidePayouts={!!(driver as any)?.fleetManagerId && !(driver as any)?.isFleetManager}
            isFleetAssigned={(driver as any)?.isFleetManager && aj.assignedFleetDriverId && aj.assignedFleetDriverId !== driver?.id}
            onPress={() => navigation.navigate('ActiveJob', { jobId: aj.id, job: aj })}
          />
        ))}

      {/* Quick actions — 3 cards with soft-bg round icon chips */}
      <View style={styles.actionsGrid}>
        <QuickAction
          icon="clipboard-outline"
          label="Find Jobs"
          color={RD.maroon}
          soft={RD.maroonSoft}
          onPress={() => navigation.navigate('Jobs')}
        />
        <QuickAction
          icon="school-outline"
          label="Training"
          color={RD.amber}
          soft={RD.amberSoft}
          onPress={() => navigation.navigate('Training')}
        />
        <QuickAction
          icon="wallet-outline"
          label="Earnings"
          color={RD.money}
          soft={RD.moneySoft}
          onPress={() => navigation.navigate('History')}
        />
        <QuickAction
          icon="cube-outline"
          label="Storage"
          color={RD.maroon}
          soft={RD.maroonSoft}
          onPress={() => navigation.navigate('Storage')}
        />
      </View>

      {/* WooCommerce Sync Status */}
      {wooSynced !== null && (
        <View style={styles.syncRow}>
          <View style={[styles.syncDot, wooSynced ? styles.syncDotConnected : styles.syncDotOffline]} />
          <Text style={styles.syncText}>
            {wooSynced ? 'Connected to WooCommerce' : 'Offline Mode'}
          </Text>
        </View>
      )}

      {/* New Job Offer Banner */}
      <JobOfferBanner
        visible={!!newJobOffer}
        job={newJobOffer}
        onPress={() => { setNewJobOffer(null); navigation.navigate('Jobs'); }}
        onDismiss={() => setNewJobOffer(null)}
      />
    </ScrollView>

      {/* How Commonplace works — shown once, on first app open */}
      <WelcomeModal visible={showWelcome} firstName={firstName} onClose={dismissWelcome} />

      {/* Pickup / expectations reminder — shown once per app open */}
      <PickupReminderModal visible={showPickupReminder} onDismiss={() => setShowPickupReminder(false)} />

      {/* Add to Home Screen — bottom sheet */}
      {showA2HS && (
        <View style={styles.a2hsOverlay} pointerEvents="box-none">
          <TouchableOpacity style={styles.a2hsBackdrop} onPress={dismissA2HS} activeOpacity={1} />
          <View style={styles.a2hsSheet}>
            <View style={styles.a2hsHandle} />
            <Text style={styles.a2hsTitle}>Install Commonplace</Text>
            {hasNativePrompt ? (
              <>
                <Text style={styles.a2hsDesc}>Add the app to your home screen for quick access and a better experience.</Text>
                <TouchableOpacity style={styles.a2hsInstallBtn} onPress={handleAddToHomeScreen} activeOpacity={0.8}>
                  <Ionicons name="download-outline" size={20} color="#fff" />
                  <Text style={styles.a2hsInstallText}>Install App</Text>
                </TouchableOpacity>
              </>
            ) : isIOS ? (
              <>
                <Text style={styles.a2hsDesc}>
                  Tap the{' '}
                  <Ionicons name="share-outline" size={16} color="#007AFF" />{' '}
                  <Text style={{ fontWeight: '800', color: '#007AFF' }}>Share</Text> button
                  {'\n'}in Safari's toolbar, then tap{'\n'}
                  <Text style={{ fontWeight: '800' }}>"Add to Home Screen"</Text>
                </Text>
                <View style={styles.a2hsArrowWrap}>
                  <Ionicons name="arrow-down" size={32} color="#007AFF" />
                </View>
              </>
            ) : (
              <>
                <Text style={styles.a2hsDesc}>
                  Tap the{' '}
                  <Ionicons name="ellipsis-vertical" size={16} color="#333" />{' '}
                  menu in your browser, then tap{'\n'}
                  <Text style={{ fontWeight: '800' }}>"Add to Home Screen"</Text> or{' '}
                  <Text style={{ fontWeight: '800' }}>"Install App"</Text>
                </Text>
              </>
            )}
            <TouchableOpacity onPress={dismissA2HS} style={styles.a2hsDismissBtn}>
              <Text style={styles.a2hsDismissText}>{hasNativePrompt ? 'Not Now' : 'Dismiss'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
}

function truncateAddress(addr?: string, maxLen = 35): string {
  if (!addr) return 'N/A';
  return addr.length > maxLen ? addr.slice(0, maxLen) + '...' : addr;
}

// True when the job's scheduled time falls on the device's local "today".
function isScheduledToday(iso?: string | null, timeZone?: string | null): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return false;
  // Compare the calendar day in the JOB's timezone (the window text is already rendered in
  // pickupTimezone) — comparing in the device tz made the TODAY badge contradict the date text
  // for out-of-tz jobs / near midnight.
  const opts: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit', ...(timeZone ? { timeZone } : {}) };
  const fmt = (x: Date) => x.toLocaleDateString('en-CA', opts); // en-CA → YYYY-MM-DD, stable
  return fmt(d) === fmt(new Date());
}

function ActiveJobCard({ activeJob, onPress, hidePayouts, isFleetAssigned, scheduledToday }: { activeJob: any; onPress: () => void; hidePayouts?: boolean; isFleetAssigned?: boolean; scheduledToday?: boolean }) {
  // Use the same payout field the rest of the app uses (`payoutAmount` + leg rates) — the old
  // `payout/driverPayout/price` chain matched none of the real job fields, so the card showed
  // no payout (or the gross order price).
  // payoutAmount is ALREADY this driver's leg pay: the server swaps the pickup/delivery rate
  // in for a leg driver, and strips pickupRate/deliveryRate from every driver payload, so
  // reading those here always fell through.
  const payout = activeJob.payoutAmount ?? activeJob.payout ?? activeJob.driverPayout;

  // Pickup → delivery route, city-level (matches v5's "City, ST → City, ST" line).
  const from = truncateAddress(activeJob.pickupAddress ?? activeJob.sellerAddress, 24);
  const to = truncateAddress(activeJob.deliveryAddress ?? activeJob.buyerAddress, 24);
  // Show the driver-facing (buffered) arrival window, not the seller's raw window.
  const whenStr = fmtDriverWindow(activeJob, true);
  const isStorage = activeJob.categorySlug === 'storage';
  const eyebrow = isFleetAssigned ? 'Fleet assigned' : isStorage ? 'Store to Sell · pickup' : 'Active delivery';

  return (
    <View style={[styles.activeJobCard, scheduledToday && { borderWidth: 2, borderColor: RD.info }]}>
      {/* Maroon left edge */}
      <View style={styles.ajEdge} />
      <View style={styles.ajBody}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Text style={styles.ajEyebrow}>{eyebrow}</Text>
          {scheduledToday && (
            <View style={{ backgroundColor: RD.info, borderRadius: 6, paddingHorizontal: 7, paddingVertical: 2 }}>
              <Text style={{ color: '#fff', fontSize: 10, fontWeight: '800', fontFamily: fonts.semiBold }}>TODAY</Text>
            </View>
          )}
        </View>
        <Text style={styles.ajProductTitle} numberOfLines={2}>
          {activeJob.productTitle}
        </Text>
        <Text style={styles.ajRoute} numberOfLines={1}>
          {from} → {to}
        </Text>

        {whenStr && (
          <Text style={{ fontSize: 12, fontWeight: '600', marginTop: 4, color: scheduledToday ? RD.info : RD.textMuted, fontFamily: fonts.semiBold }}>
            <Ionicons name="time-outline" size={12} color={scheduledToday ? RD.info : RD.textMuted} /> Pickup {whenStr}
          </Text>
        )}

        {/* Payout — hidden for fleet members */}
        {payout != null && !hidePayouts && (
          <Text style={styles.ajPayout}>${Number(payout).toFixed(2)} payout</Text>
        )}

        {/* Resume button */}
        <TouchableOpacity style={styles.ajButton} onPress={onPress} activeOpacity={0.85}>
          <Text style={styles.ajButtonText}>{isFleetAssigned ? 'View status' : 'Resume delivery'}</Text>
          <Ionicons name="chevron-forward" size={16} color="#fff" />
        </TouchableOpacity>
      </View>
    </View>
  );
}

function QuickAction({ icon, label, color, soft, onPress }: { icon: any; label: string; color: string; soft: string; onPress: () => void }) {
  return (
    <TouchableOpacity style={styles.actionCard} onPress={onPress} activeOpacity={0.7}>
      <View style={[styles.actionIconWrap, { backgroundColor: soft }]}>
        <Ionicons name={icon} size={21} color={color} />
      </View>
      <Text style={styles.actionLabel}>{label}</Text>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container:        { flex: 1, backgroundColor: RD.bg },
  content:          { padding: spacing.lg, paddingTop: Platform.OS === 'ios' ? 60 : 40, paddingBottom: Platform.OS === 'ios' ? 110 : 88 },

  // Header — greeting + online/offline toggle
  header:           { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.xl },
  headerNameWrap:   { flex: 1 },
  greeting:         { fontSize: 13, color: RD.textMuted, fontWeight: '500', fontFamily: fonts.regular },
  nameRow:          { flexDirection: 'row', alignItems: 'center', gap: 8 },
  name:             { fontSize: 24, fontWeight: '700', color: RD.text, marginTop: 2, fontFamily: fonts.heavy, letterSpacing: -0.4 },
  nameSupportBadge: { flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: RD.maroon, borderRadius: 11, paddingHorizontal: 7, height: 20, marginTop: 4 },
  nameSupportBadgeText: { color: '#fff', fontSize: 11, fontWeight: '800', fontFamily: fonts.semiBold },
  supportBell:      { width: 42, height: 42, borderRadius: 21, backgroundColor: RD.maroonSoft, justifyContent: 'center', alignItems: 'center' },
  supportBellDot:   { position: 'absolute', top: 4, right: 4, minWidth: 16, height: 16, borderRadius: 8, paddingHorizontal: 4, backgroundColor: RD.maroon, borderWidth: 1.5, borderColor: RD.bg, justifyContent: 'center', alignItems: 'center' },
  supportBellDotText: { color: '#fff', fontSize: 9, fontWeight: '800' },
  toggleWrap:       { flexDirection: 'row', alignItems: 'center', gap: 9 },
  toggleText:       { fontSize: 13, fontWeight: '600' },
  toggleTextOnline: { color: RD.money },
  toggleTextOffline:{ color: RD.textMuted },
  toggleTrack:      { width: 46, height: 28, borderRadius: 14, padding: 3, flexDirection: 'row' },
  toggleTrackOn:    { backgroundColor: RD.money, justifyContent: 'flex-end' },
  toggleTrackOff:   { backgroundColor: RD.g15, justifyContent: 'flex-start' },
  toggleKnob:       { width: 22, height: 22, borderRadius: 11, backgroundColor: '#fff', shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 2, shadowOffset: { width: 0, height: 1 }, elevation: 2 },
  toggleKnobOn:     {},
  toggleKnobOff:    {},

  // "This week" earnings hero — dark card
  heroCard:         { backgroundColor: RD.ink, borderRadius: 18, padding: 18, marginBottom: spacing.md, shadowColor: '#140C10', shadowOpacity: 0.18, shadowRadius: 24, shadowOffset: { width: 0, height: 8 }, elevation: 8 },
  heroEyebrow:      { fontSize: 11, fontWeight: '700', color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: 0.7, fontFamily: fonts.semiBold },
  heroAmountRow:    { flexDirection: 'row', alignItems: 'flex-end', marginTop: 4 },
  heroAmount:       { fontSize: 36, fontWeight: '700', color: '#fff', letterSpacing: -0.3, fontFamily: fonts.heavy, fontVariant: ['tabular-nums'] },
  heroAmountCents:  { fontSize: 22, fontWeight: '700', color: 'rgba(255,255,255,0.5)', fontFamily: fonts.heavy, fontVariant: ['tabular-nums'], marginBottom: 2 },
  heroSub:          { fontSize: 12.5, color: 'rgba(255,255,255,0.6)', marginTop: 4, fontFamily: fonts.regular },

  // Active delivery — white card, maroon left edge, Resume button
  activeJobCard: {
    flexDirection: 'row',
    backgroundColor: RD.surface,
    borderRadius: 18,
    overflow: 'hidden',
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: RD.border,
    shadowColor: '#140C10',
    shadowOpacity: 0.04,
    shadowRadius: 2,
    shadowOffset: { width: 0, height: 1 },
    elevation: 1,
  },
  ajEdge:           { width: 4, backgroundColor: RD.maroon },
  ajBody:           { flex: 1, padding: 16 },
  ajEyebrow:        { fontSize: 11, fontWeight: '700', color: RD.maroon, textTransform: 'uppercase', letterSpacing: 0.7, fontFamily: fonts.semiBold },
  ajProductTitle:   { fontSize: 15.5, fontWeight: '700', color: RD.text, marginTop: 5, fontFamily: fonts.heavy },
  ajRoute:          { fontSize: 13, color: RD.textMuted, marginTop: 2, fontFamily: fonts.regular },
  ajPayout:         { fontSize: 13, fontWeight: '700', color: RD.money, marginTop: 6, fontFamily: fonts.semiBold },
  ajButton: {
    marginTop: 13,
    height: 40,
    alignSelf: 'flex-start',
    paddingHorizontal: 16,
    borderRadius: 12,
    backgroundColor: RD.maroon,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  ajButtonText:     { fontSize: 13.5, fontWeight: '600', color: '#fff', fontFamily: fonts.semiBold },

  // Quick actions — 3 cards with soft-bg round icon chips
  actionsGrid:      { flexDirection: 'row', gap: 11, marginBottom: spacing.md },
  actionCard:       { flex: 1, backgroundColor: RD.surface, borderRadius: 18, padding: 14, alignItems: 'center', gap: 10, shadowColor: '#140C10', shadowOpacity: 0.04, shadowRadius: 2, shadowOffset: { width: 0, height: 1 }, elevation: 1, borderWidth: 1, borderColor: RD.border },
  actionIconWrap:   { width: 42, height: 42, borderRadius: 21, justifyContent: 'center', alignItems: 'center' },
  actionLabel:      { fontSize: 12.5, fontWeight: '600', color: RD.textSec, fontFamily: fonts.semiBold },

  // Sync status
  syncRow:            { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: spacing.lg },
  syncDot:            { width: 6, height: 6, borderRadius: 3 },
  syncDotConnected:   { backgroundColor: RD.money },
  syncDotOffline:     { backgroundColor: RD.g30 },
  syncText:           { fontSize: 12, color: RD.textMuted, fontWeight: '500' },

  // Refresh
  refreshRow:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: spacing.sm },
  refreshText:      { fontSize: 13, color: RD.textMuted, fontWeight: '500' },

  // Add to Home Screen — bottom sheet
  a2hsOverlay:      { position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, justifyContent: 'flex-end' as const },
  a2hsBackdrop:     { position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.45)' },
  a2hsSheet:        { backgroundColor: RD.surface, borderTopLeftRadius: 20, borderTopRightRadius: 20, paddingHorizontal: 20, paddingTop: 12, paddingBottom: 32, alignItems: 'center' as const, maxWidth: 500, width: '100%' as any, alignSelf: 'center' as const },
  a2hsHandle:       { width: 36, height: 4, borderRadius: 2, backgroundColor: RD.g15, marginBottom: 14 },
  a2hsTitle:        { fontSize: 17, fontWeight: '700' as const, color: RD.text, textAlign: 'center' as const, marginBottom: 6 },
  a2hsDesc:         { fontSize: 14, color: RD.textSec, textAlign: 'center' as const, lineHeight: 21, marginBottom: 16 },
  a2hsInstallBtn:   { flexDirection: 'row' as const, alignItems: 'center' as const, justifyContent: 'center' as const, gap: 8, height: 54, width: '100%' as any, borderRadius: 16, backgroundColor: RD.ink, marginBottom: 8 },
  a2hsInstallText:  { fontSize: 16, fontWeight: '600' as const, color: '#fff' },
  a2hsDismissBtn:   { paddingVertical: 8, paddingHorizontal: 20 },
  a2hsDismissText:  { fontSize: 14, fontWeight: '600' as const, color: RD.textMuted },
  a2hsArrowWrap:    { marginBottom: 4, marginTop: 2 },
});
